import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle, X } from 'lucide-react';
import { Button } from '@/components/ui/Button';

interface DeleteConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  itemName?: string;
  loading?: boolean;
}

export const DeleteConfirmModal: React.FC<DeleteConfirmModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  itemName,
  loading = false,
}) => {
  const [confirmText, setConfirmText] = useState('');
  const [error, setError] = useState('');

  // Reset quando il modal si apre/chiude
  useEffect(() => {
    if (isOpen) {
      setConfirmText('');
      setError('');
    }
  }, [isOpen]);

  const handleConfirm = () => {
    if (confirmText === 'ELIMINA') {
      setError('');
      onConfirm();
    } else {
      setError('Devi digitare "ELIMINA" per confermare');
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && confirmText === 'ELIMINA') {
      handleConfirm();
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 bg-black/60"
          onClick={onClose}
        />

        {/* Modal */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          className="relative bg-white rounded-xl shadow-2xl max-w-lg w-full"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-red-50">
            <div className="flex items-center gap-3">
              <AlertTriangle size={32} className="text-red-600" />
              <h2 className="text-2xl font-bold text-red-900">{title}</h2>
            </div>
            <button
              onClick={onClose}
              disabled={loading}
              className="p-2 hover:bg-red-100 rounded-lg transition-colors disabled:opacity-50"
            >
              <X size={24} className="text-red-700" />
            </button>
          </div>

          {/* Body */}
          <div className="p-6">
            {itemName && (
              <div className="mb-4">
                <p className="text-lg font-semibold text-gray-900 mb-1">
                  {itemName}
                </p>
              </div>
            )}

            <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4 mb-6">
              <p className="text-gray-700 mb-2">{message}</p>
              <p className="text-sm font-bold text-red-700 mt-3">
                ⚠️ Questa operazione è IRREVERSIBILE!
              </p>
            </div>

            <p className="text-sm text-gray-700 mb-3">
              Per confermare, digita "<strong>ELIMINA</strong>" nel campo sottostante:
            </p>

            <input
              type="text"
              value={confirmText}
              onChange={(e) => {
                setConfirmText(e.target.value);
                setError('');
              }}
              onKeyPress={handleKeyPress}
              placeholder="Scrivi ELIMINA"
              className="w-full px-4 py-2 border-2 border-red-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent disabled:opacity-50"
              disabled={loading}
              autoComplete="off"
              autoFocus
            />

            {error && (
              <motion.p
                initial={{ opacity: 0, y: -5 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-sm text-red-600 mt-2"
              >
                {error}
              </motion.p>
            )}
          </div>

          {/* Footer */}
          <div className="flex gap-3 p-6 bg-gray-50 border-t border-gray-200">
            <Button
              variant="secondary"
              onClick={onClose}
              disabled={loading}
              fullWidth
            >
              Annulla
            </Button>
            <Button
              variant="danger"
              onClick={handleConfirm}
              loading={loading}
              disabled={confirmText !== 'ELIMINA'}
              fullWidth
            >
              ELIMINA DEFINITIVAMENTE
            </Button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};
